<template>
    <div class="salary-schedule-table">
        <div class="table-scroll">
            <table>
                <thead>
                    <tr>
                        <th class="exp-head">Yrs</th>
                        <th v-for="lane in lanes" :key="lane.id" :class="laneClass(lane)" :title="laneTitle(lane)" @click="selectLane(lane)">
                            <span class="tier">{{ lane.tier }}</span>
                            <span v-if="lane.id === currentLaneId" class="lane-tag">You</span>
                            <span v-else-if="lane.id === targetLaneId" class="lane-tag target">Next</span>
                            <span v-else-if="selectableById[lane.id]" class="lane-tag pick">{{ selectableById[lane.id].requiredHours }} hrs</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="exp in expRows" :key="exp">
                        <th class="exp-cell" :class="{'is-current-row': exp === currentExp}">{{ exp }}</th>
                        <td v-for="lane in lanes" :key="lane.id" :class="cellClass(lane, exp)" :title="laneTitle(lane)" @click="selectLane(lane)">{{ cellValue(lane, exp) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="scale-legend" v-if="position">
            <span class="legend-swatch current"></span>
            <span>Where you are &mdash; {{ position.current.tier }} at {{ position.exp }} {{ position.exp === 1 ? 'year' : 'years' }}</span>
            <template v-if="target">
                <span class="legend-swatch target"></span>
                <span>Next column &mdash; {{ target.tier }}</span>
            </template>
            <span v-if="targets.length > 1" class="legend-hint">Select any highlighted column to see what that move is worth.</span>
        </div>
        <div class="scale-legend" v-else>
            Add your degree, current column and years above to highlight your placement.
        </div>
    </div>
</template>

<script>
    /**
     * The district's published salary schedule as a raw grid — lanes across,
     * years of service down, exactly the published steps (blank where a
     * schedule skips a step). The student's current cell is highlighted; the
     * charted target lane is tinted; reachable lanes are clickable and emit
     * `select` so the charts follow. Port of the v2 quote builder's ScaleTable.
     */
    export default {
        name: "SalaryScheduleTable",
        props: {
            scale: {type: Object, required: true},
            position: {type: Object, default: null}, // computePosition() result
            target: {type: Object, default: null},   // the lane the charts show
            targets: {type: Array, default: () => []}, // reachable lanes
        },
        computed: {
            lanes() {
                return [...(this.scale.columns || [])]
                    .filter(c => c.steps && c.steps.length)
                    .sort((a, b) => a.position - b.position);
            },
            expRows() {
                const set = new Set();
                for (const lane of this.lanes) for (const s of lane.steps) set.add(s.exp);
                return [...set].sort((a, b) => a - b);
            },
            currentLaneId() {
                return this.position && this.position.current ? this.position.current.id : null;
            },
            targetLaneId() {
                return this.target && this.target.lane ? this.target.lane.id : null;
            },
            // The published step actually paid: highest step at or below the
            // years of service (schedules freeze at the top)
            currentExp() {
                if (!this.position) return null;
                const lane = this.lanes.find(l => l.id === this.currentLaneId);
                if (!lane) return null;
                let match = lane.steps[0] ? lane.steps[0].exp : null;
                for (const s of lane.steps) {
                    if (s.exp <= this.position.exp) match = s.exp;
                    else break;
                }
                return match;
            },
            selectableById() {
                const map = {};
                for (const t of this.targets) if (t.lane && t.lane.id != null) map[t.lane.id] = t;
                return map;
            }
        },
        methods: {
            selectLane(lane) {
                const t = this.selectableById[lane.id];
                if (t) this.$emit('select', t);
            },
            laneTitle(lane) {
                const t = this.selectableById[lane.id];
                return t ? 'See what moving to ' + lane.tier + ' is worth — ' + t.requiredHours + ' clock hours' : '';
            },
            laneClass(lane) {
                return {
                    'is-current-lane': lane.id === this.currentLaneId,
                    'is-target-lane': lane.id === this.targetLaneId,
                    'is-selectable': !!this.selectableById[lane.id],
                };
            },
            stepFor(lane, exp) {
                return lane.steps.find(s => s.exp === exp);
            },
            cellValue(lane, exp) {
                const step = this.stepFor(lane, exp);
                return step ? '$' + Math.round(step.value).toLocaleString('en-US') : '';
            },
            cellClass(lane, exp) {
                return {
                    'is-current-cell': lane.id === this.currentLaneId && exp === this.currentExp,
                    'is-current-lane': lane.id === this.currentLaneId,
                    'is-target-lane': lane.id === this.targetLaneId,
                    'is-selectable': !!this.selectableById[lane.id],
                    'is-empty': !this.stepFor(lane, exp),
                };
            }
        }
    };
</script>

<style lang="scss" scoped>
    $blue: #336699;
    $blue-tint: #e9f0f7;
    $blue-tint-light: #f4f8fb;
    $green: #2e7d4f;
    $green-tint: #e7f4ec;
    $green-tint-light: #f3faf5;

    .table-scroll {
        overflow-x: auto;
        max-width: 100%;
        border: 1px solid #dfe5ec;
        border-radius: 8px;
    }

    table {
        border-collapse: collapse;
        width: 100%;
        font-size: 0.82rem;
    }

    th, td {
        padding: 0.45rem 0.8rem;
        text-align: right;
        white-space: nowrap;
        border-bottom: 1px solid #eef2f6;
        font-variant-numeric: tabular-nums;
    }

    thead th {
        background: #f7f9fb;
        color: #33475b;
        font-weight: 700;
        border-bottom: 2px solid #dfe5ec;
        vertical-align: bottom;

        .tier {
            display: block;
        }

        .lane-tag {
            display: inline-block;
            margin-top: 3px;
            font-size: 0.6rem;
            font-weight: 800;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            padding: 1px 7px;
            border-radius: 999px;
            background: $blue;
            color: #fff;

            &.target {
                background: $green-tint;
                color: $green;
            }

            &.pick {
                background: transparent;
                border: 1px solid #cfd8e2;
                color: #7a8a9d;
            }
        }

        &.is-current-lane {
            background: $blue-tint;
        }

        &.is-target-lane {
            background: $green-tint;
        }

        &.is-selectable {
            cursor: pointer;

            &:hover {
                background: darken($green-tint, 4%);
            }
        }
    }

    .exp-head {
        text-align: center;
    }

    tbody {
        th.exp-cell {
            background: #f7f9fb;
            color: #5f6b7a;
            font-weight: 600;
            text-align: center;
            width: 3rem;

            &.is-current-row {
                background: $blue-tint;
                color: $blue;
            }
        }

        td {
            color: #33475b;

            &.is-current-lane {
                background: $blue-tint-light;
            }

            &.is-target-lane {
                background: $green-tint-light;
            }

            &.is-selectable {
                cursor: pointer;

                &:hover {
                    background: $green-tint;
                }
            }

            &.is-current-cell {
                background: $blue;
                color: #fff;
                font-weight: 800;
                border-radius: 6px;
            }

            &.is-empty {
                color: #c5ccd5;
            }
        }

        tr:last-child th,
        tr:last-child td {
            border-bottom: none;
        }
    }

    .scale-legend {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.45rem;
        margin-top: 0.75rem;
        font-size: 0.78rem;
        color: #666d7a;

        .legend-swatch {
            width: 0.85rem;
            height: 0.85rem;
            border-radius: 4px;
            flex-shrink: 0;

            &.current {
                background: $blue;
            }

            &.target {
                background: $green-tint;
                border: 1px solid #cfe5d6;
                margin-left: 0.9rem;
            }
        }

        .legend-hint {
            margin-left: auto;
            color: #8a96a3;
        }
    }
</style>
